import { theme } from 'antd';
import type { GlobalToken } from 'antd';
import { ReactNode, CSSProperties } from 'react';
import styled from 'styled-components';

export type EyebrowSize = 's' | 'm';
export type EyebrowTone = 'primary' | 'default';

export interface EyebrowProps {
  /** Eyebrow text — a short, uppercase kicker shown above a title. */
  children: ReactNode;
  /** Font size. Defaults to 'm'. */
  size?: EyebrowSize;
  /** Color. 'primary' uses the brand color, 'default' inherits from context. */
  tone?: EyebrowTone;
  /** Additional class name. */
  className?: string;
  /** Additional style. */
  style?: CSSProperties;
}

const sizeFontSize = (token: GlobalToken, size: EyebrowSize): number =>
  size === 's' ? token.fontSizeSM : token.fontSize;

const toneColor = (
  token: GlobalToken,
  tone: EyebrowTone,
): string | undefined =>
  tone === 'primary' ? token.colorPrimary : token.colorTextSecondary;

interface StyledEyebrowProps {
  $token: GlobalToken;
  $size: EyebrowSize;
  $tone: EyebrowTone;
}

const StyledEyebrow = styled.span<StyledEyebrowProps>`
  font-family: ${({ $token }) => $token.fontFamily};
  font-size: ${({ $token, $size }) => sizeFontSize($token, $size)}px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ $token, $tone }) => toneColor($token, $tone)};
`;

/**
 * Eyebrow — a small uppercase kicker label shown above a title or section
 * (e.g. a step indicator). Purely typographic; layout spacing (margin, grid
 * placement) is left to the consumer since it varies by context.
 *
 * @example
 * ```tsx
 * import { Eyebrow } from '@libertify/ui';
 *
 * <Eyebrow>Step 1</Eyebrow>
 * <Eyebrow size="s" tone="default">Account</Eyebrow>
 * ```
 */
export const Eyebrow = ({
  children,
  size = 'm',
  tone = 'primary',
  className,
  style,
}: EyebrowProps) => {
  const { token } = theme.useToken();
  return (
    <StyledEyebrow
      $token={token}
      $size={size}
      $tone={tone}
      className={className}
      style={style}
    >
      {children}
    </StyledEyebrow>
  );
};

export default Eyebrow;
